import { News } from '@interfaces/news';
import {Event, EventSeriesInfo, RecurrenceType} from '@interfaces/event';
import { Owner } from '@interfaces/owner';
import { Location } from '@interfaces/location';
import { parseJSON } from 'date-fns';
import { Video } from '@interfaces/video';

export interface ImageAttributes {
  id: number;
  url: string;
  focal: { x: number; y: number } | null;
}

export class TimelineEntry {
  created_at: Date;
  description: string | null;
  is_ai_generated: boolean | null;
  event_date_start: Date;
  event_date_end: Date | null;
  id: string;
  images: string[];
  images_attributes: ImageAttributes[];
  owner: Owner;
  title: string;
  slug: string | null;
  type: 'event' | 'news';
  locations: Location[];
  likes: number;
  is_liked_by_user: boolean;
  video: Video | null;
  recurrence_type?: RecurrenceType;
  recurrence_end_date?: string | null;
  parent_event_id?: string | null;
  is_part_of_series?: boolean;
  is_recurring_instance?: boolean;
  recurring_instances_count?: number;
  series_info?: EventSeriesInfo;

  constructor(entry: News | Event) {
    this.id = entry.id;
    this.images = entry.images;
    this.images_attributes = entry.images_attributes;
    this.owner = entry.owner;
    this.title = entry.title;
    this.slug = entry.slug;
    this.created_at = parseJSON(entry.created_at);
    this.description = entry.description;
    this.is_ai_generated = entry.is_ai_generated ?? null;
    // @ts-ignore
    this.event_date_start = entry.hasOwnProperty('event_date_start') ? entry.event_date_start as Date : new Date();
    this.event_date_end = null;
    this.type = 'news';
    this.locations = entry.locations;
    this.likes = entry.likes;
    this.is_liked_by_user = entry.is_liked_by_user;

    if ((entry as Event).event_date_start) {
      this.type = 'event';
      const event = entry as Event;
      this.event_date_start = parseJSON(event.event_date_start);
      this.recurrence_type = event.recurrence_type ?? 'none';
      this.recurrence_end_date = event.recurrence_end_date ?? null;
      this.parent_event_id = event.parent_event_id ?? null;
      this.is_part_of_series = event.is_part_of_series ?? false;
      this.is_recurring_instance = event.is_recurring_instance ?? false;
      this.recurring_instances_count = event.recurring_instances_count ?? 0;
      this.series_info = event.series_info;
    }
    if ((entry as Event).event_date_end && (entry as Event).event_date_end !== null) {
      this.event_date_end = parseJSON(<string>(entry as Event).event_date_end);
    }

    if (entry.hasOwnProperty('video') && (entry as News).video) {
      this.video = (entry as News).video;
    }
  }

  getMainImage(): ImageAttributes | null {
    if (this.images_attributes && this.images_attributes.length > 0) {
      return this.images_attributes[0];
    }

    if (this.images && this.images.length > 0) {
      return {
        id: 0,
        url: this.images[0],
        focal: null,
      };
    }

    return null;
  }

  getMainImageUrl(): string | null {
    if (this.getMainImage()) {
      return this.getMainImage()!.url;
    }

    if (this.owner.header) {
      return this.owner.header;
    }

    return null;
  }

  getLink(): string {
    const path = this.type === 'event' ? 'events' : 'news';

    if (this.owner.type === 'app') {
      return this.owner.slug;
    }

    if (!this.slug) {
      return `/${path}/${this.id}`;
    }

    return `/${path}/${this.owner.slug}/${this.slug}`;
  }

  toNews(): News {
    return {
      id: this.id,
      images: this.images,
      images_attributes: this.images_attributes,
      owner: this.owner,
      title: this.title,
      description: this.description,
      is_ai_generated: this.is_ai_generated,
      slug: this.slug,
      created_at: this.created_at.toISOString(),
      locations: this.locations,
      likes: this.likes,
      is_liked_by_user: this.is_liked_by_user,
      video: this.video || null,
    } as News;
  }

  toEvent(): Event {
    return {
      id: this.id,
      images: this.images,
      images_attributes: this.images_attributes,
      owner: this.owner,
      title: this.title,
      description: this.description,
      is_ai_generated: this.is_ai_generated,
      slug: this.slug,
      created_at: this.created_at.toISOString(),
      event_date_start: this.event_date_start.toISOString(),
      event_date_end: this.event_date_end?.toISOString() || null,
      locations: this.locations,
      likes: this.likes,
      is_liked_by_user: this.is_liked_by_user,
      video: this.video || null,
      recurrence_type: this.recurrence_type ?? 'none',
      recurrence_end_date: this.recurrence_end_date ?? null,
      parent_event_id: this.parent_event_id ?? null,
      is_part_of_series: this.is_part_of_series ?? false,
      is_recurring_instance: this.is_recurring_instance ?? false,
      recurring_instances_count: this.recurring_instances_count ?? 0,
      series_info: this.series_info,
    };
  }
}
